import i18n from '@/i18n';
import { GraphRequestError, graphError } from '@/services/graph/graph-request-error';
import { GRAPH_RETRY_OPTIONS } from '@/services/graph/graph-retry';
import { INLINE_ATTACHMENT_MAX_BYTES } from '@/services/graph/graph-mail-service';
import { withRetry } from '@/utils/withRetry';
import type { FileSystemItem } from '../../../types';
import { formatBytes } from '../../../views/details-view/format-bytes';
import { runWithConcurrency } from '../../upload/concurrencyPool';

// Bounded, not unbounded: the downloads are the only concurrent half of this, and 4 is the same value
// the folder-tree upload and the transfer batch both use — enough to hide latency, low enough not to
// invite Graph throttling. It also caps peak memory, since a pool slot holds its blob until that item's
// attach lands: at most this many file bodies exist at once.
const DEFAULT_DOWNLOAD_CONCURRENCY = 4;

/**
 * The pre-flight ceiling on a selection's RAW bytes.
 *
 * The binding limit is not Graph's 150 MB per-attachment cap. It is the mailbox's max message size —
 * Exchange Online defaults to 25 MB and it is tenant-configurable — applied to the BASE64-INFLATED
 * payload, roughly 1.37x raw once MIME overhead is counted. 25 / 1.37 lands just under 18.
 *
 * Advisory by design. It exists so an obviously-doomed selection costs zero requests, not to be the
 * authority: Graph enforces the real limit, and a tenant may have raised or lowered it.
 */
export const MAX_TOTAL_ATTACHMENT_BYTES = 18 * 1024 * 1024;

export type MailItemStatus =
  | 'queued'
  | 'downloading'
  | 'attaching'
  | 'done'
  | 'failed';

/**
 * One row in the progress list. Mirrors UploadProgressItem, which solves the same problem for folder
 * trees — with `failureReason` added, so a partial failure's per-file explanation lives on the row
 * itself rather than in a separate error list something has to reconcile against it.
 *
 * `path` and not just `name`, unlike the upload panel's single-tree case: a selection can span mounts,
 * so two rows can legitimately carry the same name and the list still needs a stable key.
 *
 * No byte percentage on purpose. Download progress cannot be measured without reading `response.body`
 * incrementally, and a bar covering only the upload half would lie about the first half. Two phases per
 * item is enough that a multi-file run never looks frozen. Known weakness: one 80 MB file shows a
 * spinner for a minute-plus — the existing upload panel has the identical weakness, so this is not a
 * regression, and the cheap fix later is an onProgress callback on uploadInChunks, which would improve
 * the drive upload panel for free too.
 */
export type MailProgressItem = {
  path: string;
  name: string;
  status: MailItemStatus;
  failureReason?: string;
};

export type AttachmentBytes = {
  blob: Blob;
  contentType: string;
};

/**
 * One of exactly three terminal states.
 *
 * 'completed' means the DRAFT EXISTS — offered with "Öppna i Outlook" even when every single attachment
 * failed. The draft is a draft: the user is about to look at it and can see what is in it, and throwing
 * away seven successful uploads to punish one failure is hostile.
 *
 * 'cancelled' is classified distinctly from 'failed' rather than collapsed into it, because the two
 * differ in what happened to the mailbox: cancelling deletes the draft, failing never made one.
 *
 * `canRetry` is set only when the failure was token acquisition. A blocked or declined consent popup is
 * recoverable from a FRESH user gesture (a button in the modal), which is not true of any other failure
 * here.
 */
export type CreateMailDraftResult =
  | { phase: 'completed'; webLink: string; succeeded: number; failed: number }
  | { phase: 'cancelled' }
  | { phase: 'failed'; reason: string; canRetry: boolean };

export interface CreateMailDraftDeps {
  /**
   * R5's up-front acquisition. Called FIRST, before anything else awaits, because MSAL's fallback for a
   * scope the app login never requested is a popup — and after our own awaits, transient user activation
   * is gone and Firefox/Safari block it.
   */
  acquireToken: () => Promise<unknown>;
  /**
   * Deliberately signal-free. An aborted create POST may still have created the message server-side,
   * leaving an orphan draft whose id was never learned and therefore can never be deleted. Letting the
   * one short request land and then deleting is the only version of this that cannot leak.
   */
  createDraft: () => Promise<{ id: string; webLink: string }>;
  getDownloadUrl: (item: FileSystemItem, signal: AbortSignal) => Promise<string>;
  fetchBytes: (url: string, signal: AbortSignal) => Promise<AttachmentBytes>;
  attachSmall: (
    messageId: string,
    name: string,
    contentType: string,
    blob: Blob,
    signal: AbortSignal,
  ) => Promise<unknown>;
  attachLarge: (
    messageId: string,
    name: string,
    contentType: string,
    blob: Blob,
    signal: AbortSignal,
  ) => Promise<unknown>;
  /** Must not be signal-aware — see deleteDraft's own comment in graph-mail-service.ts (R4). */
  deleteDraft: (messageId: string) => Promise<void>;
  concurrency?: number;
}

const isAbortError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: string }).name === 'AbortError';

/**
 * Pure: why one file did not make it into the draft, or null when it did not actually fail.
 *
 * Same shape and spirit as summarizeTransferFailures and summarizeDeleteFailures — a pure exported
 * function returning `string | null`, narrowing on `GraphRequestError.status` rather than string-parsing
 * a message. Distinct reasons matter here: "too large", "not found", "access denied" and "throttled"
 * each tell the user something different about what to do next, where a generic "3 attachments failed"
 * tells them nothing.
 *
 * Returns null for an abort, so a cancelled run is never counted or reported as a failure.
 */
export const describeMailFailure = (error: unknown): string | null => {
  const { t } = i18n.global;
  if (isAbortError(error)) return null;

  if (error instanceof GraphRequestError) {
    // 401 while ATTACHING is about the mailbox, not the file — the draft it is attaching to lives in one.
    // Deliberately not worded as a permission problem: see describeDraftFailure's reasoning, where the
    // same status means the identity has no mailbox despite holding a valid, correctly-scoped token.
    if (error.status === 401) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedMailAccess');
    }
    if (error.status === 403) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedAccessDenied');
    }
    if (error.status === 404) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedNotFound');
    }
    if (error.status === 413) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedTooLarge');
    }
    if (error.status === 429 || error.status === 503) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedThrottled');
    }
  }

  return t('fileManager.tools.outlookMail.createMailDraft.failedGeneric');
};

/**
 * Pure: why the draft itself could not be created — a whole-run failure, so never null.
 *
 * The 404 case is `MailboxNotEnabledForRESTAPI`: the account has no Exchange mailbox at all. The role
 * gate on `tool:outlook-mail-copy` makes this rare (guests here are temporary Entra accounts with no
 * mailbox, and they do not get the permission) — rare, not impossible, and "det gick inte" would send
 * someone hunting for a network problem that does not exist.
 */
export const describeDraftFailure = (error: unknown): string => {
  const { t } = i18n.global;
  if (error instanceof GraphRequestError) {
    /*
     * 401 and 404 both mean "Exchange would not serve this identity a mailbox", so they share a message.
     *
     * That is counter-intuitive for a 401 and was established the hard way: a token can be perfectly
     * valid — right `aud`, `Mail.ReadWrite` present in `scp`, right `tid`/`appid` — and still get 401 with
     * an EMPTY body and no WWW-Authenticate. That is not Graph's own error shape; it is Exchange Online
     * rejecting the call downstream because the identity has no mailbox to authenticate into (a B2B guest,
     * or a member with no Exchange licence). 404 MailboxNotEnabledForRESTAPI is the neighbouring case
     * where a mailbox exists but is unlicensed or hosted on-premises.
     *
     * Earlier this pointed at Mail.ReadWrite, which actively misdirects: by the time a 401 reaches here,
     * the scope demonstrably WAS granted. A missing scope surfaces as 403 below instead. Whatever
     * distinguishing detail exists is on the console — graphErrorWithDetail logs Graph's `code`, and
     * graph-mail-service logs the token claims plus any WWW-Authenticate challenge.
     */
    if (error.status === 401 || error.status === 404) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedNoMailbox');
    }
    if (error.status === 403) {
      return t('fileManager.tools.outlookMail.createMailDraft.failedMailForbidden');
    }
  }
  return t('fileManager.tools.outlookMail.createMailDraft.failedDraft');
};

/**
 * Pure: the pre-flight size guard's message, or null when the selection is within the ceiling.
 *
 * A missing `size` counts as zero, i.e. "unknown, allow" — the guard is advisory and Graph enforces the
 * real limit, so refusing on incomplete metadata would block a send that would have worked.
 *
 * The wording is "för stort för att skickas", not "kan inte bifogas": the limit is the recipient
 * mailbox's message size, not anything about attaching, and that distinction is what makes the number
 * make sense to whoever reads it.
 */
export const describeOversizedSelection = (
  target: FileSystemItem[],
  maxTotalBytes = MAX_TOTAL_ATTACHMENT_BYTES,
): string | null => {
  const { t } = i18n.global;
  const total = target.reduce((sum, item) => sum + (item.size ?? 0), 0);
  if (total <= maxTotalBytes) return null;
  return t('fileManager.tools.outlookMail.createMailDraft.tooLargeToSend', {
    total: formatBytes(total),
    max: formatBytes(maxTotalBytes),
  });
};

/**
 * Reads a file's bytes from its pre-signed download URL.
 *
 * `maxRetries: 1`, deliberately NOT the default GRAPH_RETRY_OPTIONS. Nothing in this repo has ever read
 * SharePoint bytes into JS before — DetailsView navigates to the URL, thumbnails go into an <img>,
 * previews into an iframe — so a CORS rejection here is a real possibility, and it surfaces as a bare
 * TypeError, which isRetryableGraphError classifies as a network failure and therefore RETRYABLE. The
 * default policy would burn four attempts and ~3.5s of backoff per file on something that cannot
 * succeed, looking to the user exactly like a network outage.
 *
 * If that turns out to be what happens, the documented fallback is
 * `GET /drives/{driveId}/items/{itemId}/content` with the Sites.Selected bearer, letting fetch follow the
 * 302. That route is NOT ruled out: "Redirect is not allowed for a preflight request" fires when the
 * OPTIONS request is redirected, not the actual GET. It would also collapse this two-request download to
 * one.
 *
 * `.blob()` and not `.arrayBuffer()`: Chromium backs a Blob with blob storage that spills to disk past a
 * threshold, and `blob.slice()` is a lazy view rather than a copy — which is what makes chunk retry work
 * without holding a second copy of the bytes.
 */
export async function fetchAttachmentBytes(
  url: string,
  signal?: AbortSignal,
): Promise<AttachmentBytes> {
  return withRetry(
    async () => {
      const response = await fetch(url, signal ? { signal } : {});

      if (!response.ok) {
        throw graphError('Failed to download file contents', response);
      }

      return {
        blob: await response.blob(),
        // Passed through to the attachment rather than defaulted, or Outlook labels everything
        // application/octet-stream and OWA refuses to preview even a PDF inline.
        contentType:
          response.headers?.get?.('Content-Type') || 'application/octet-stream',
      };
    },
    { ...GRAPH_RETRY_OPTIONS, maxRetries: 1, signal },
  );
}

/**
 * Creates a blank Outlook draft with the selected files attached as byte copies.
 *
 * Vue-free and dependency-injected, mirroring uploadFolderTree — its closest precedent — so the whole
 * sequence is unit-testable with hand-written fakes and no fetch stubbing.
 *
 * Byte copies rather than `referenceAttachment` links, because in dev the files live in one Azure AD
 * tenant and the mailbox in another: a SharePoint link cannot be permissioned across that boundary.
 * Bytes are tenant-agnostic and work in both environments.
 */
export async function createMailDraft(
  target: FileSystemItem[],
  deps: CreateMailDraftDeps,
  signal: AbortSignal,
  onStatusChange?: (
    path: string,
    status: MailItemStatus,
    failureReason?: string,
  ) => void,
): Promise<CreateMailDraftResult> {
  /**
   * Every status report goes through here. A subscriber is a UI concern observing an operation, and must
   * never be able to fail the operation it is only watching — least of all in a way that would surface to
   * the user as "the mail failed". This is also what makes the pool worker below total: with reporting
   * unable to throw, every remaining await inside it is individually guarded.
   */
  const report = (
    path: string,
    status: MailItemStatus,
    failureReason?: string,
  ) => {
    try {
      onStatusChange?.(path, status, failureReason);
    } catch {
      // The observer's problem, not this operation's.
    }
  };

  // FIRST, before anything else awaits (R5).
  try {
    await deps.acquireToken();
  } catch (error) {
    if (isAbortError(error)) return { phase: 'cancelled' };
    const { t } = i18n.global;
    return {
      phase: 'failed',
      reason: t('fileManager.tools.outlookMail.createMailDraft.failedToken'),
      canRetry: true,
    };
  }

  if (signal.aborted) return { phase: 'cancelled' };

  // Before a single request is spent, so an obviously-doomed selection costs nothing.
  const oversized = describeOversizedSelection(target);
  if (oversized) {
    return { phase: 'failed', reason: oversized, canRetry: false };
  }

  for (const item of target) report(item.path, 'queued');

  let draft: { id: string; webLink: string };
  try {
    draft = await deps.createDraft();
  } catch (error) {
    if (isAbortError(error)) return { phase: 'cancelled' };
    return {
      phase: 'failed',
      reason: describeDraftFailure(error),
      canRetry: false,
    };
  }

  // The cancel-race window the plan names: Avbryt landed while the create POST was in flight. The id
  // exists now, so it can be cleaned up — which is exactly why createDraft is not signal-aware.
  if (signal.aborted) return finishCancelled(deps, draft.id);

  let succeeded = 0;
  let failed = 0;

  // Attaches are serialized onto one chain while downloads stay concurrent (R1). Exchange applies
  // optimistic concurrency PER MESSAGE ITEM, so two workers POSTing to the same message's /attachments
  // come back 409 / ErrorIrresolvableConflict — and 409 is not retryable, so it would be fatal per file,
  // with a different set of files failing on every run. Unreproducible in QA, which is the worst kind.
  //
  // The chain never rejects: every attach catches its own error. So this can be built with plain
  // `.then` without an unhandled rejection ever escaping.
  let attachChain: Promise<void> = Promise.resolve();

  await runWithConcurrency(
    target,
    deps.concurrency ?? DEFAULT_DOWNLOAD_CONCURRENCY,
    async (item) => {
      // runWithConcurrency has no abort concept — its `while (cursor < items.length)` loop keeps
      // handing out work after an abort — so the worker checks for itself.
      if (signal.aborted) return;

      // TOTAL: every await below is individually guarded and `report` cannot throw, so nothing escapes
      // this worker. That is what keeps the pool's `firstError` from ever firing and collapsing a
      // cancelled run into a failed one. Same shape useFileManagerTransfer's own worker uses.
      report(item.path, 'downloading');
      let bytes: AttachmentBytes;
      try {
        const url = await deps.getDownloadUrl(item, signal);
        bytes = await deps.fetchBytes(url, signal);
      } catch (error) {
        const reason = describeMailFailure(error);
        if (reason) {
          failed += 1;
          report(item.path, 'failed', reason);
        }
        return;
      }

      const attachment = attachChain.then(async () => {
        if (signal.aborted) return;

        report(item.path, 'attaching');
        try {
          // blob.size, NEVER item.size. `size` is optional on BaseFileSystemItem (and for a directory
          // it is the RECURSIVE subtree size), and `undefined > 3_000_000` evaluates to false — so an
          // unknown-size file would silently take the inline base64 path and 413 AFTER paying for a
          // full download.
          const attach =
            bytes.blob.size > INLINE_ATTACHMENT_MAX_BYTES
              ? deps.attachLarge
              : deps.attachSmall;
          await attach(draft.id, item.name, bytes.contentType, bytes.blob, signal);
          succeeded += 1;
          report(item.path, 'done');
        } catch (error) {
          const reason = describeMailFailure(error);
          if (reason) {
            failed += 1;
            report(item.path, 'failed', reason);
          }
        }
      });
      // The chain the NEXT item links onto is deliberately a neutralised copy: a rejection here would
      // otherwise propagate through every later `.then` and fail the whole remaining batch rather than
      // just this item.
      attachChain = attachment.catch(() => {});

      // Awaited inside the worker, so this pool slot — and this item's blob — stays occupied until its
      // attach lands. That is what bounds peak memory to `concurrency` file bodies rather than the
      // whole selection's, given there is no streaming (attaching is N ranged PUTs, not one pipeable
      // body).
      await attachment;
    },
  );

  if (signal.aborted) return finishCancelled(deps, draft.id);

  return { phase: 'completed', webLink: draft.webLink, succeeded, failed };
}

/**
 * Deletes the abandoned draft and reports 'cancelled'.
 *
 * Unlike a SharePoint copy — which completes server-side regardless, hence CopyMonitorError
 * ('abandoned') — this operation is ours to undo, and a half-built draft the user explicitly cancelled
 * is litter in their real mailbox.
 *
 * A failed cleanup is swallowed: the run is already over from the user's side, and a 'cancelled' outcome
 * turning into an error toast about a mailbox they were not looking at would explain nothing. The
 * DELETE is not signal-aware, which is the whole point — see deleteDraft.
 */
async function finishCancelled(
  deps: Pick<CreateMailDraftDeps, 'deleteDraft'>,
  messageId: string,
): Promise<CreateMailDraftResult> {
  try {
    await deps.deleteDraft(messageId);
  } catch {
    // Swallowed by design.
  }
  return { phase: 'cancelled' };
}
